import logger from '../../../utils/logger';

function waitForIceGatheringComplete(pc) {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") {
      resolve();
      return;
    }

    const timeout = setTimeout(() => {
      logger.warn("ICE gathering timeout — continuing early");
      pc.removeEventListener("icegatheringstatechange", onChange);
      resolve(); // continue even if not fully complete
    }, 1000); // wait max 1 second (adjust if needed)

    const onChange = () => {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(timeout);
        pc.removeEventListener("icegatheringstatechange", onChange);
        resolve();
      }
    };

    pc.addEventListener("icegatheringstatechange", onChange);
  });
}

export function createWebrtcAdapter(src, onStreamError) {
  let pc = null;
  let videoEl = null;
  let statsTimeout = null;

  return {
    name: "webrtc",

    async attach(video) {
      try {
        videoEl = video;

        pc = new RTCPeerConnection({
          sdpSemantics: "unified-plan",
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
        });

        pc.addTransceiver("video", { direction: "recvonly" });

        pc.ontrack = (e) => {
          const stream = e.streams[0] ?? new MediaStream([e.track]);
          videoEl.srcObject = stream;
          videoEl.muted = true;
          videoEl.setAttribute("playsinline", "");
          videoEl.autoplay = true;

          videoEl.addEventListener(
            "loadedmetadata",
            () => {
              videoEl?.play().catch(() => {});
            },
            { once: true }
          );
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Wait for ICE gathering but don’t block too long
        await waitForIceGatheringComplete(pc);

        const res = await fetch(src.url, {
          method: "POST",
          headers: {
            "Content-Type": "application/sdp",
            ...(src.token ? { Authorization: `Bearer ${src.token}` } : {}),
          },
          body: offer.sdp || "",
        });

        const answerSdp = await res.text();
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

        // Optional: logs for debugging (only in development)
        pc.addEventListener("icegatheringstatechange", () =>
          logger.log("iceGathering=", pc.iceGatheringState)
        );
        pc.addEventListener("iceconnectionstatechange", () =>
          logger.log("iceConnection=", pc.iceConnectionState)
        );
        pc.addEventListener("connectionstatechange", () =>
          logger.log("connection=", pc.connectionState)
        );
        pc.onicecandidate = (e) => logger.log("iceCandidate:", !!e.candidate);

        videoEl.onloadedmetadata = () => logger.log("loadedmetadata");
        videoEl.onplaying = () => logger.log("playing");

        statsTimeout = setTimeout(async () => {
          // Check if pc is still valid before calling getStats
          if (pc && pc.connectionState !== "closed") {
            try {
              const stats = await pc.getStats();
              stats.forEach((r) => {
                if (r.type === "inbound-rtp" && r.kind === "video") {
                  logger.log(
                    "bytesReceived",
                    r.bytesReceived,
                    "framesDecoded",
                    r.framesDecoded
                  );
                }
              });
            } catch (error) {
              logger.warn("Failed to get WebRTC stats:", error);
            }
          }
          statsTimeout = null;
        }, 1000);
      } catch (error) {
        onStreamError()
        console.error("Failed to attach WebRTC stream:", error);
        if (pc) {
          pc.close();
          pc = null;
        }
        const newerror = new Error("Camera stream is not working");
        // throw newerror;
      }
    },

    async detach() {
      // Clear any pending stats timeout
      if (statsTimeout) {
        clearTimeout(statsTimeout);
        statsTimeout = null;
      }

      if (pc) {
        // Stop all sender tracks
        pc.getSenders().forEach((s) => {
          if (s.track) {
            s.track.stop();
          }
        });
        
        // Stop all receiver tracks
        pc.getReceivers().forEach((r) => {
          if (r.track) {
            r.track.stop();
          }
        });
        
        // Close peer connection
        pc.close();
        pc = null;
      }

      if (videoEl) {
        // Stop all tracks from the video element's stream
        if (videoEl.srcObject) {
          const stream = videoEl.srcObject;
          if (stream instanceof MediaStream) {
            stream.getTracks().forEach((track) => {
              track.stop();
            });
          }
        }
        // Clear video source
        videoEl.srcObject = null;
        videoEl = null;
      }
    },

    async play() {
      try {
        await videoEl?.play();
      } catch (error) {
        logger.warn("Failed to play WebRTC video:", error);
      }
    },

    async pause() {
      try {
        videoEl?.pause();
      } catch (error) {
        logger.warn("Failed to pause WebRTC video:", error);
      }
    },
  };
}
